(function () {
  'use strict';

  // Run only on Searchanise results page
  if (window.location.pathname !== '/pages/search-results-page') return;

  const APPROVE_MIN_PRICE = 1500;
  const PRODUCT_CACHE = new Map();
  const PROCESS_DELAY = 100;
  let processTimer = null;

  // Get and cache Shopify product data
  async function getProductData(handle) {
    if (!handle) return null;
    if (PRODUCT_CACHE.has(handle)) return PRODUCT_CACHE.get(handle);

    try {
      const response = await fetch(`/products/${encodeURIComponent(handle)}.js`);
      if (!response.ok) return null;

      const product = await response.json();
      PRODUCT_CACHE.set(handle, product);
      return product;
    } catch (error) {
      console.error('[Approve/Searchanise] Product fetch error:', error);
      return null;
    }
  }

  // Shopify stores prices in cents
  function shopifyPriceToDollars(price) {
    return Number(price) / 100;
  }

  // Get currently selected Searchanise variant
  function getSelectedVariantId(productCard) {
    return productCard.getAttribute('data-product-variant');
  }

  // Get Shopify product handle
  function getProductHandle(productCard) {
    const link = productCard.querySelector('a.snize-view-link');
    if (!link) return null;

    const href = link.getAttribute('href');
    if (!href) return null;

    try {
      const url = new URL(href, window.location.origin);
      const match = url.pathname.match(/^\/products\/(.+?)(?:\/)?$/);
      return match ? decodeURIComponent(match[1]) : null;
    } catch {
      return null;
    }
  }

  // Searchanise uses "Add to Cart" for available variants and
  // "View Product" for unavailable variants.
  function isSearchaniseProductAvailable(productCard) {
    const button = productCard.querySelector('.snize-add-to-cart-button');
    if (!button) return false;

    return button.textContent.trim().toLowerCase() === 'add to cart';
  }

  // Remove Approve button
  function removeApproveButton(productCard) {
    const existing = productCard.querySelector(':scope > approve-button');
    if (existing) existing.remove();

    delete productCard.dataset.approveVariant;
  }

  // Create/update Approve button
  function addApproveButton(productCard, variant, title) {
    const existing = productCard.querySelector(':scope > approve-button');

    // Already showing the correct variant
    if (
      existing &&
      productCard.dataset.approveVariant === String(variant.id)
    ) {
      return;
    }

    if (existing) existing.remove();

    const approveButton = document.createElement('approve-button');

    approveButton.setAttribute('application-type', 'embedded_app');
    approveButton.setAttribute('price', String(shopifyPriceToDollars(variant.price)));
    approveButton.setAttribute('model', title);
    approveButton.setAttribute('qty', '1');
    approveButton.setAttribute('type', 'new_product');

    // Approve button styling
    approveButton.style.display = 'block';
    approveButton.style.setProperty('margin-top', '5px', 'important');
    approveButton.style.setProperty('margin-bottom', '10px', 'important');
    approveButton.style.scale = '0.95';
    approveButton.style.setProperty('--button_color', '#C8102E');
    approveButton.style.setProperty('--button_height', '26px');
    approveButton.style.setProperty('--button_border_radius', '3px');
    approveButton.style.setProperty('--button_font', 'Helvetica');
    approveButton.style.setProperty('--button_font_size', '14px');
    approveButton.style.setProperty('--button_powered_by_size', '12px');

    /*
     * IMPORTANT:
     * Append outside .snize-view-link so clicking Approve
     * doesn't navigate to the product page.
     */
    productCard.appendChild(approveButton);

    // Reapply margins after Approve initializes
    setTimeout(() => {
      approveButton.style.setProperty('margin-top', '5px', 'important');
      approveButton.style.setProperty('margin-bottom', '10px', 'important');
    }, 100);

    productCard.dataset.approveVariant = String(variant.id);
  }

  // Process one Searchanise product
  async function processProductCard(productCard) {
    if (!productCard?.matches('.snize-product')) return;

    // Remove Approve if Searchanise says the variant is unavailable
    if (!isSearchaniseProductAvailable(productCard)) {
      removeApproveButton(productCard);
      return;
    }

    const variantId = getSelectedVariantId(productCard);
    const handle = getProductHandle(productCard);
    if (!variantId || !handle) return;

    const product = await getProductData(handle);
    if (!product?.variants) return;

    const variant = product.variants.find(
      item => String(item.id) === String(variantId)
    );

    if (!variant) {
      removeApproveButton(productCard);
      return;
    }

    const price = shopifyPriceToDollars(variant.price);

    // Approve requires minimum $1,500
    if (price < APPROVE_MIN_PRICE) {
      removeApproveButton(productCard);
      return;
    }

    const titleElement = productCard.querySelector('.snize-title');
    const title = titleElement
      ? titleElement.textContent.trim()
      : product.title;

    addApproveButton(productCard, variant, title);
  }

  // Process all Searchanise products
  function processAllProducts() {
    document.querySelectorAll('.snize-product').forEach(processProductCard);
  }

  // Debounce processing when Searchanise makes multiple DOM changes
  function scheduleProcessing() {
    clearTimeout(processTimer);
    processTimer = setTimeout(processAllProducts, PROCESS_DELAY);
  }

  /*
   * Watch Searchanise for:
   * - New products
   * - Filters
   * - Sorting
   * - Pagination
   * - New searches
   * - Variant changes
   * - CTA changes
   */
  const observer = new MutationObserver(mutations => {
    let relevantChange = false;

    mutations.forEach(mutation => {
      if (mutation.addedNodes?.length) {
        for (const node of mutation.addedNodes) {
          if (
            node.nodeType === 1 &&
            (
              node.matches?.('.snize-product') ||
              node.querySelector?.('.snize-product')
            )
          ) {
            relevantChange = true;
            break;
          }
        }
      }

      if (
        mutation.type === 'attributes' &&
        mutation.attributeName === 'data-product-variant'
      ) {
        relevantChange = true;
      }

      if (
        mutation.type === 'childList' &&
        mutation.target?.closest?.('.snize-product')
      ) {
        relevantChange = true;
      }
    });

    if (relevantChange) scheduleProcessing();
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-product-variant']
  });

  // Handle Searchanise variation dropdowns
  document.addEventListener('change', event => {
    if (!event.target.matches?.('.snize-option')) return;

    const productCard = event.target.closest('.snize-product');
    if (!productCard) return;

    setTimeout(() => {
      processProductCard(productCard);
    }, PROCESS_DELAY);
  });

  // Initial processing
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', processAllProducts);
  } else {
    processAllProducts();
  }
})();